/**
 * Role-based access control configuration.
 *
 * IMPORTANT: Role names must stay in sync with the backend at:
 * packages/runtimes/api/src/modules/auth/permissions.ts
 *
 * Defines three permission tiers:
 * - admin: Full access to all user management operations
 * - moderator: Limited access (view users, ban/unban, manage sessions)
 * - user: No admin permissions
 */

export const ROLES = ["admin", "moderator", "user"] as const;

export type Role = (typeof ROLES)[number];

/**
 * Role hierarchy for permission checks.
 * Higher number = more permissions.
 */
export const ROLE_HIERARCHY: Record<Role, number> = {
  admin: 3,
  moderator: 2,
  user: 1,
};

/**
 * Check if a role has at least the specified minimum role level.
 */
export function hasMinimumRole(userRole: Role, minimumRole: Role): boolean {
  return ROLE_HIERARCHY[userRole] >= ROLE_HIERARCHY[minimumRole];
}

/**
 * Check if a string is a valid role.
 */
export function isValidRole(role: string | null | undefined): role is Role {
  return ROLES.includes(role as Role);
}

/**
 * Get a valid role from a string, defaulting to "user".
 */
export function parseRole(role: string | null | undefined): Role {
  return isValidRole(role) ? role : "user";
}
